import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import groupBy from 'lodash/groupBy';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentOrPreviousGroup, Member } from '../../../../../../api/groups';
import { Module } from '../../../../../../api/module';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import HorizontalScrollContext from '../../../../../../shared-components/table/context/HorizontalScrollContext';
import { Result } from '../../../../../../utils/results';
import { ExportData } from '../../../api/export';
import { getLessonResults, getThemeResults, LessonResults, ThemeResults } from '../../../api/results';
import { ScorableItem } from '../../../api/scorable-items';
import { useFilters } from '../../../context/filter-context/FilterContext';
import { ResultModule, SchoolYearValue, useResultsDataContext } from '../../../context/ResultsDataContext';
import { mapLessonResults, ResultWithGroup } from '../../per-student/hooks/useResults';
import { groupBelongsToSelectedSchoolYear } from '../../../utils/groupBelongsToSelectedSchoolYear';
import { TableData } from '../../types';
import {
  buildDetailViewGroupedTableRows,
  buildTableRows,
  createTableHeader,
  createTableHeaderForDetailView,
} from './table-data-utils';
import useStudioGroup from '../../../../../../hooks/useStudioGroup';

async function fetchLessonResults(
  selectedSchoolYear: SchoolYearValue,
  selectedModule: Module,
  scorableItems: Record<string, Record<string, ScorableItem[]>>,
  selectedGroups: CurrentOrPreviousGroup[],
  studioGroup: StudioGroup,
) {
  const [schoolYearStart, schoolYearEnd] = selectedSchoolYear.id.split('-');

  const getLessonResultsSearchParams = new URLSearchParams({
    includeFreeExercises: 'true',
    includeCompletedAssignments: 'true',
    includeUncompletedAssignments: 'true',
    exerciseIds: scorableItems[selectedSchoolYear.id][selectedModule.id].map(item => item.id.split('-')[0]).join(','),
    ...(studioGroup === 'VO' ? { schoolYearStart, schoolYearEnd } : {}),
  });

  const lessonsData = await Promise.all(
    selectedGroups.map(group => getLessonResults(selectedModule!.id, group.id, getLessonResultsSearchParams)),
  );

  return lessonsData.reduce((acc, lessonData, index) => {
    acc[selectedGroups[index].id] = lessonData;
    return acc;
  }, {} as Record<string, LessonResults>);
}

async function fetchThemeResults(
  selectedSchoolYear: SchoolYearValue,
  selectedModule: ResultModule,
  scorableItems: Record<string, Record<string, ScorableItem[]>>,
  selectedGroups: CurrentOrPreviousGroup[],
  studioGroup: StudioGroup,
) {
  const [schoolYearStart, schoolYearEnd] = selectedSchoolYear.id.split('-');

  const getThemeResultsSearchParams = new URLSearchParams({
    includeFreeExercises: 'true',
    includeCompletedAssignments: 'true',
    includeUncompletedAssignments: 'true',
    exerciseIds: scorableItems[selectedSchoolYear.id][selectedModule.id].map(item => item.id.split('-')[0]).join(','),
    ...(studioGroup === 'VO' ? { schoolYearStart, schoolYearEnd } : {}),
  });

  const themeData = await Promise.all(
    selectedGroups.map(group =>
      getThemeResults(selectedModule!.id.split('-')[0], group.id, getThemeResultsSearchParams),
    ),
  );

  return themeData.reduce((acc, themeData, index) => {
    acc[selectedGroups[index].id] = themeData;
    return acc;
  }, {} as Record<string, ThemeResults>);
}

export type ExtendedResult = Result & { scorableItemId: string; groupId: string; nodeId: string };

export default function useTableData({ detailView }: { detailView: boolean }): {
  data: TableData;
  loading: boolean;
  noGroups: boolean;
  noGroupsForFilter: boolean;
  noScorableItems: boolean;
  noScorableItemsForFilter: boolean;
  usedScorableItems: (ScorableItem & { isAssignment: boolean })[];
  resultsWithGroup: ResultWithGroup[];
  exportData: ExportData;
  fetchScoreData: () => Promise<void>;
} {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const studioGroup = useStudioGroup();
  const { groups, scorableItemsPerSchoolYearPerModule, loading, scorableItems } = useResultsDataContext();
  const { selectedModule, selectedSchoolYear, selectedSchool, selectedScorableItems, selectedMembers, selectedGroups } =
    useFilters();
  const [resultsLoading, setResultsLoading] = useState(true);
  const [resultDataPerGroup, setResultDataPerGroup] = useState<Record<string, LessonResults | ThemeResults>>({});
  const handleError = useThrowAsyncError();
  const { horizontalScroll } = useContext(HorizontalScrollContext);

  const resultsWithGroup: ResultWithGroup[] = Object.entries(resultDataPerGroup).flatMap(([groupId, lessonData]) =>
    mapLessonResults(lessonData.scores, groupId),
  );

  const displayedGroups = useMemo(() => {
    return (selectedGroups || []).filter(
      x =>
        x.acceptedMemberCount > 0 &&
        groupBelongsToSelectedSchoolYear(x, selectedSchoolYear?.id) &&
        x.school.id === selectedSchool?.id,
    );
  }, [selectedGroups, selectedSchoolYear?.id, selectedSchool?.id]);

  const fetchScoreData = useCallback(
    async (current = false) => {
      if (Object.values(loading).some(x => x)) return;
      if (!selectedSchoolYear || !selectedModule) return;

      if (!scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id]?.[selectedModule.id]) {
        setResultDataPerGroup({});
        setResultsLoading(false);
        return;
      }

      setResultsLoading(true);

      try {
        const newResultDataPerGroup = selectedModule.isVirtualExerciseModule
          ? await fetchThemeResults(
              selectedSchoolYear,
              selectedModule,
              scorableItemsPerSchoolYearPerModule,
              selectedGroups,
              studioGroup,
            )
          : await fetchLessonResults(
              selectedSchoolYear,
              selectedModule,
              scorableItemsPerSchoolYearPerModule,
              selectedGroups,
              studioGroup,
            );

        if (current) {
          setResultDataPerGroup(newResultDataPerGroup);
        }
      } catch (error) {
        handleError(error);
      } finally {
        if (current) {
          setResultsLoading(false);
        }
      }
    },
    [
      loading,
      selectedSchoolYear,
      selectedModule,
      scorableItemsPerSchoolYearPerModule,
      selectedGroups,
      studioGroup,
      handleError,
    ],
  );

  useEffect(() => {
    let current = true;

    fetchScoreData(current);

    return () => {
      current = false;
    };
  }, [
    selectedModule,
    selectedSchoolYear,
    selectedSchool,
    selectedGroups,
    loading,
    scorableItemsPerSchoolYearPerModule,
    handleError,
    fetchScoreData,
  ]);

  const allResults = useMemo<ExtendedResult[]>(() => {
    return Object.entries(resultDataPerGroup).flatMap(([groupId, groupData]) =>
      Object.entries(groupData.scores).flatMap(([nodeId, members]) =>
        Object.entries(members).flatMap<ExtendedResult>(([memberId, scoresOfStudent]) => {
          return scoresOfStudent.map(score => ({
            ...score,
            memberId,
            nodeId,
            groupId,
            scorableItemId: `${score.exerciseId}-${score.isAssignment}`,
          }));
        }),
      ),
    );
  }, [resultDataPerGroup]);

  const usedScorableItems = useMemo(() => {
    if (
      !selectedSchoolYear ||
      !selectedModule ||
      loading.scorableItems ||
      !scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id] ||
      !scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id][selectedModule.id]
    )
      return [];

    const resultsByExerciseId = groupBy(allResults, 'scorableItemId');
    const membersByCompositeKey = Object.entries(selectedMembers).reduce((acc, [groupId, members]) => {
      const byMemberId = members.reduce((acc, member) => {
        acc[`${groupId}-${member.id}`] = member;
        return acc;
      }, {} as Record<string, Member>);

      return { ...acc, ...byMemberId };
    }, {} as Record<string, Member>);

    return scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id][selectedModule.id]
      .filter(
        item =>
          !selectedModule.isVirtualExerciseModule ||
          Object.values(resultDataPerGroup).some(scoreData => scoreData.mediaLinks[item.id.split('-')[0]]),
      )
      .filter(
        item =>
          !selectedScorableItems.length ||
          selectedScorableItems.some(scorableItem => scorableItem.id.includes(item.id)),
      )
      .filter(item => {
        const results = resultsByExerciseId[item.id];

        if (!results?.length) return false;

        return results.some(result => membersByCompositeKey[`${result.groupId}-${result.memberId}`]);
      });
  }, [
    allResults,
    loading.scorableItems,
    resultDataPerGroup,
    scorableItemsPerSchoolYearPerModule,
    selectedMembers,
    selectedModule,
    selectedSchoolYear,
    selectedScorableItems,
  ]);

  const dataIsLoading = useMemo(() => {
    return (
      loading.groups ||
      loading.members ||
      loading.scorableItems ||
      resultsLoading ||
      !selectedModule ||
      !selectedSchoolYear
    );
  }, [loading, resultsLoading, selectedModule, selectedSchoolYear]);

  const exportData = useMemo(() => {
    const result: ExportData = {};

    // Create sets for faster lookups
    const selectedGroupIds = new Set(selectedGroups.map(group => group.id));
    const selectedMembersByGroup = new Map(
      Object.entries(selectedMembers).map(([groupId, members]) => [groupId, new Set(members.map(member => member.id))]),
    );

    for (const [groupId, lessonData] of Object.entries(resultDataPerGroup)) {
      if (!selectedGroupIds.has(groupId)) continue;

      const selectedMembersInGroup = selectedMembersByGroup.get(groupId);
      if (!selectedMembersInGroup) continue;

      for (const members of Object.values(lessonData.scores)) {
        for (const [memberId, scores] of Object.entries(members)) {
          if (!selectedMembersInGroup.has(memberId)) continue;

          if (!result[memberId]) {
            result[memberId] = [];
          }

          for (const item of selectedScorableItems) {
            const x = scores.find(x => x.exerciseId === item.id.split('-')[0] && x.isAssignment === item.isAssignment);
            if (!x) continue;

            result[memberId].push({
              exerciseId: x.exerciseId,
              exerciseType: x.exerciseType,
              isAssignment: x.isAssignment,
              isZeroScore: x.isZeroScore,
              amountOfItems: x.amountOfItems,
              achievedAt: x.achievedAt,
              isLti: x.isLti,
              needsGrading: x.needsGrading,
              score: x.score,
              session: x.session,
              duration: x.duration,
              status: x.status,
            });
          }
        }
      }
    }

    return result;
  }, [resultDataPerGroup, selectedGroups, selectedMembers, selectedScorableItems]);

  const tableData: TableData = useMemo(() => {
    if (dataIsLoading) {
      return {
        header: createTableHeader(t, usedScorableItems, language as SupportedUserLanguage),
        rows: [],
      };
    }

    if (detailView) {
      const scorableItem = selectedScorableItems[0];
      return {
        header: createTableHeaderForDetailView(t('PAGES.RESULTS.PER_EXERCISE.TABLE_HEADER'), t),
        rows: scorableItem
          ? buildDetailViewGroupedTableRows(
              resultDataPerGroup,
              displayedGroups,
              selectedMembers,
              scorableItem,
              language as SupportedUserLanguage,
              horizontalScroll,
            )
          : [],
      };
    }

    return {
      header: createTableHeader(t, usedScorableItems, language as SupportedUserLanguage),
      rows: buildTableRows(
        resultDataPerGroup,
        displayedGroups,
        selectedMembers,
        usedScorableItems,
        language as SupportedUserLanguage,
      ),
    };
  }, [
    dataIsLoading,
    detailView,
    t,
    usedScorableItems,
    language,
    resultDataPerGroup,
    displayedGroups,
    selectedMembers,
    selectedScorableItems,
    horizontalScroll,
  ]);

  return {
    noGroups: groups.filter(x => x.acceptedMemberCount > 0).length === 0,
    noGroupsForFilter: !displayedGroups.length,
    noScorableItems: scorableItems.length === 0,
    noScorableItemsForFilter: !usedScorableItems.length,
    data: tableData,
    loading: dataIsLoading,
    usedScorableItems,
    resultsWithGroup,
    exportData,
    fetchScoreData,
  };
}
